<template>
  <section :class="$options.name" class="tw:flex flex-column tw:gap-4">
    <div class="tw:flex tw:gap-2 tw:py-1 tw:sticky tw:top-0 tw:z-[1] tw:bg-white">
      <EdroneDatepicker
        :disabled="
          !state.can({
            type: 'FETCH_ACTIVITIES',
            filters: activityFilters,
            range: activityRange,
            search: activitySearch,
          }) || isDemo
        "
        :disabled-date="disabledAfterToday"
        :shortcuts="[datesDatepickerShortcuts[0], datesDatepickerShortcuts[1], datesDatepickerShortcuts[2]]"
        :value="activityRange"
        style="flex: 1"
        type="range"
        unique-id="bb013ef5-2b2d-49eb-8cf7-40971273a2a4"
        @input="
          send({ type: 'UPDATE_ACTIVITY_RANGE', data: $event });
          send({ type: 'FETCH_ACTIVITIES', filters: activityFilters, range: activityRange, search: activitySearch });
        "
      />
      <EdroneDropdown
        :disabled="
          !state.can({
            type: 'FETCH_ACTIVITIES',
            filters: activityFilters,
            range: activityRange,
            search: activitySearch,
          }) || isDemo
        "
        :show-border="false"
        close-trigger-class-name="EdroneButton"
        icon="fa-light fa-bars-filter"
        items-width="250px"
        only-icon
        unique-id="88878496-69c3-46cd-bc07-0b29644b6074"
        @close="handleApplyFilters"
      >
        <div>
          <div class="tw:flex flex-column tw:p-3 tw:gap-3">
            <div class="tw:caption" style="color: #545454">
              {{ trans._inbox.customer.activity.filters.types }}
            </div>
            <EdroneCheckbox
              v-for="item in availableFilters"
              :key="item.value"
              :checked="activityFilters.includes(item.value)"
              :label="item.name"
              :value="item.value"
              unique-id="0916c57f-d82d-46b2-a59e-e2d7c3968695"
              @change="
                send({
                  type: 'UPDATE_ACTIVITY_FILTERS',
                  data: item.value,
                })
              "
            />
          </div>
          <EdroneDropdownSeparator unique-id="22961844-6329-4f1c-9501-bf2b6c8acbfd" />
          <div class="tw:p-3">
            <div class="tw:body-2 tw:mb-2">{{ trans._inbox.customer.activity.filters.search.label }}</div>
            <EdroneInput
              :placeholder="trans._inbox.customer.activity.filters.search.placeholder"
              :value="activitySearch"
              unique-id="47e1193a-e5f5-4187-870b-b1c49e7c6c04"
              @input="
                send({
                  type: 'UPDATE_ACTIVITY_SEARCH',
                  data: $event,
                })
              "
            >
              <template #icon>
                <font-awesome-icon icon="fa-light fa-magnifying-glass" />
              </template>
            </EdroneInput>
          </div>
          <EdroneDropdownSeparator unique-id="c45cdd77-46b9-4837-bad9-eb1169d70644" />
          <div class="tw:p-3 tw:flex tw:gap-3">
            <EdroneButton
              size="small"
              style="width: 100%"
              type="text"
              unique-id="5ec0a6fa-758b-4f4c-9ab1-2717d0956cbb"
              @click="send({ type: 'CLEAR_ACTIVITY_FILTERS_AND_SEARCH' })"
            >
              {{ trans._inbox.customer.activity.filters.clear }}
            </EdroneButton>
            <EdroneButton
              size="small"
              style="width: 100%"
              type="outline"
              unique-id="35611bfe-1148-48ba-b5be-c032c1996265"
            >
              {{ trans._inbox.customer.activity.filters.confirm }}
            </EdroneButton>
          </div>
        </div>
      </EdroneDropdown>
    </div>
    <div v-if="showError">
      <EdroneResultState class="tw:mt-10" type="error" unique-id="86897aba-9996-43fc-8757-537b5af7b79c">
        <h3 class="tw:heading-2 tw:mb-1">{{ trans._inbox.customer.activity.results.error.title }}</h3>
        <p class="tw:body-2 tw:mb-4">{{ trans._inbox.customer.activity.results.error.description }}</p>
        <div>
          <EdroneButton
            type="outline"
            unique-id="07fbb446-b609-4d21-8b4c-f5c6c58d4c1d"
            @click="
              send({ type: 'FETCH_ACTIVITIES', filters: activityFilters, range: activityRange, search: activitySearch })
            "
          >
            {{ trans._inbox.customer.activity.results.error.retry }}
          </EdroneButton>
        </div>
      </EdroneResultState>
    </div>
    <div v-else-if="state.hasTag('fetchingActivities')">
      <EdroneResultState class="tw:mt-10" type="loading" unique-id="d1ae7cc9-bde6-4526-9c2d-41aef37cdbd5" />
    </div>
    <div v-else id="activities_list" class="tw:flex flex-column tw:gap-1">
      <template v-if="activities && activities.length">
        <!--  TODO: local pagination  -->
        <CustomerPreviewActivityItem
          v-for="(item, index) in activities.filter((filtered) => shouldShowActivity(filtered) || isDev)"
          :key="index"
          :is-selected="!!selectedActivity && selectedActivity.index === index"
          :item="item"
          @click="handleClickActivity(item, index)"
        />
      </template>
      <template v-else-if="!state.hasTag('fetchingActivities')">
        <EdroneResultState class="tw:mt-10" type="empty" unique-id="22674806-d21a-4046-8299-319be301f4ac">
          <h3 class="tw:heading-2 tw:mb-4">{{ trans._inbox.customer.activity.results.empty.title }}</h3>
          <p class="tw:body-2 tw:mb-6">{{ trans._inbox.customer.activity.results.empty.description }}</p>
        </EdroneResultState>
      </template>
    </div>

    <template v-if="state.hasTag('watchingActivityPreview') && selectedActivity">
      <CustomerPreviewActivityPreviewProductModal v-if="selectedActivity.products" @close="selectedActivity = null" />
      <CustomerPreviewActivityPreviewEmailModal
        v-else-if="selectedActivity.emailEventData"
        @close="selectedActivity = null"
      />
    </template>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from "vue";
import { disabledAfterToday } from "vue-app/datesDatepicker/datesDatepicker";
import { Activity, useCustomerPreviewActor } from "vue-app/pages/machines/machineCustomerPreview";
import CustomerPreviewActivityPreviewEmailModal from "vue-app/components/Customer/CustomerPreviewActivity/CustomerPreviewActivityPreviewEmailModal.vue";
import CustomerPreviewActivityPreviewProductModal from "vue-app/components/Customer/CustomerPreviewActivity/CustomerPreviewActivityPreviewProductModal.vue";
import CustomerPreviewActivityItem from "vue-app/components/Customer/CustomerPreviewActivity/CustomerPreviewActivityItem.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";
import EdroneDatepicker from "edrone-components/EdroneDatepicker.vue";
import EdroneDropdown from "edrone-components/EdroneDropdown.vue";
import EdroneDropdownSeparator from "edrone-components/EdroneDropdownSeparator.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneCheckbox from "edrone-components/EdroneCheckbox.vue";
import EdroneResultState from "edrone-components/EdroneResultState.vue";
import { datesDatepickerShortcuts } from "vue-app/datesDatepicker/datesDatepickerShortcuts";

export default defineComponent({
  name: "CustomerPreviewActivity",
  components: {
    EdroneInput,
    EdroneDatepicker,
    EdroneDropdown,
    EdroneDropdownSeparator,
    EdroneButton,
    EdroneCheckbox,
    EdroneResultState,
    CustomerPreviewActivityPreviewEmailModal,
    CustomerPreviewActivityPreviewProductModal,
    CustomerPreviewActivityItem,
  },
  setup() {
    const customerPreviewActor = useCustomerPreviewActor();
    const isDev = window.EDRONE_CONTEXT.user.isDev;

    const showError = computed(() => customerPreviewActor.state.value.context.fetchingActivitiesError);
    const activities = computed(() => customerPreviewActor.state.value.context.activities);
    const selectedActivity = ref<(Activity & { index: number }) | null>(null);

    const availableFilters = customerPreviewActor.state.value.context.availableActivityFilters;

    const activityFilters = computed(() => {
      return customerPreviewActor.state.value.context.activityFilters;
    });
    const activitySearch = computed(() => {
      return customerPreviewActor.state.value.context.activitySearch;
    });
    const activityRange = computed(() => {
      return customerPreviewActor.state.value.context.activityRange;
    });

    const filtersChanged = ref(false);
    watch([activitySearch, activityFilters], () => {
      filtersChanged.value = true;
    });

    const handleApplyFilters = () => {
      if (filtersChanged.value) {
        filtersChanged.value = false;
        customerPreviewActor.send({
          type: "FETCH_ACTIVITIES",
          filters: activityFilters.value,
          range: activityRange.value,
          search: activitySearch.value,
        });
      }
    };

    const handleClickActivity = (item: Activity, index: number) => {
      selectedActivity.value = { ...item, index };
      customerPreviewActor.send({ type: "OPEN_ACTIVITY_PREVIEW", details: item });
    };

    onMounted(() => {
      // Fetch activities on mount only if activities are not already fetched
      if (
        !activities.value &&
        customerPreviewActor.state.value.can({
          type: "FETCH_ACTIVITIES",
          filters: activityFilters.value,
          range: activityRange.value,
          search: activitySearch.value,
        })
      ) {
        customerPreviewActor.send({
          type: "FETCH_ACTIVITIES",
          filters: activityFilters.value,
          range: activityRange.value,
          search: activitySearch.value,
        });
      }
    });

    // Hide activity for automation received if email not received
    const shouldShowActivity = (item: Activity) => {
      return item.type !== "AUTOMATION_RECEIVED" || item.automationReceivedData?.receivedEmail;
    };

    const isDemo = computed(() => window.EDRONE_CONTEXT.isDemo);

    return {
      isDemo,
      state: customerPreviewActor.state,
      send: customerPreviewActor.send,
      isDev,
      showError,
      availableFilters,
      activityFilters,
      activityRange,
      activitySearch,
      activities,
      selectedActivity,
      datesDatepickerShortcuts,
      shouldShowActivity,
      handleApplyFilters,
      handleClickActivity,
      disabledAfterToday,
    };
  },
});
</script>
